<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import PhIcon from '@/components/PhIcon.vue'
import LabelBase from '@/components/label/LabelBase.vue'
import { useModalStore } from '@/stores/modal.store.ts'
import type { Job } from '@/types/model'
import JobImport from '@/views/page/job/JobImport.vue'
import moment from 'moment'
import { formatPhoneNumber } from '@/globals/text.ts'

// stores
const { confirmModalWithMessage, showModal } = useModalStore()

// refs
const visible = ref(false)

// misc
let dragCounter = 0

// methods
const containsJobs = (str: string): boolean => {
  return str.toLowerCase().includes('jobs')
}

const handleDrop = async (event: DragEvent) => {
  visible.value = false
  dragCounter = 0

  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    const confirm = await confirmModalWithMessage('Apakah kamu yakin akan memasukan file ini?')

    if (confirm) {
      for (const file of files) {
        try {
          if (containsJobs(file.name)) {
            const jsonFile = (await readFileAsJson(file)) as Job
            showModal('none', 'Import pekerjaan', 'Import Job', JobImport, {
              job: jsonFile,
            })
          }
        } catch (e) {
          console.log(e)
          // showModal('error', e);
        }
      }
      // const names = Array.from(files).map(file => file.name)
      // console.log('Dropped files:', names)
      // Lakukan proses dengan files sesuai kebutuhan
    }
  }
}

const readFileAsJson = (file: File) => {
  return new Promise<Job>((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const result = event.target?.result as string
        const jsonData = JSON.parse(result)

        const mapJob: Job = {
          name: jsonData['woNumber'],
          accessories: jsonData['accessoriesReceived'],
          repair_at: getDateTime(
            jsonData['Actual Start Date'],
            jsonData['Actual Start Time'],
            jsonData['createdAt'],
          ),
          collection_at: getDateTime(
            jsonData['Actual End Date'],
            jsonData['Actual End Time'],
            jsonData['createdAt'],
          ),
          actual_start_at: getDateTime(
            jsonData['Actual Start Date'],
            jsonData['Actual Start Time'],
            jsonData['createdAt'],
          ),
          actual_end_at: getDateTime(
            jsonData['Actual End Date'],
            jsonData['Actual End Time'],
            jsonData['createdAt'],
          ),
          service_info: jsonData['symptomDetail'] ?? jsonData['conditionDetail'] ?? 'RUSAK',
          repair_info: jsonData['defectDetail'],
          customer_name: jsonData['customer']['accountName'],
          customer_type: 1, // customer
          phone: formatPhoneNumber(jsonData['customer']['user']['mobilePhone']),
          address: `${jsonData['customer']['user']['billingAddress']['street']}, ${jsonData['customer']['user']['billingAddress']['village']}, ${jsonData['customer']['user']['billingAddress']['subDistrict']}, ${jsonData['customer']['user']['billingAddress']['city']},`,
          model: jsonData['asset']['model']['modelCode'],
          serial: jsonData['asset']['serialNumber'],
          purchase_date: getDateTime(jsonData['asset']['purchaseDate'], '', jsonData['createdAt']),
          warranty_type: jsonData['assetWarrantyChecked'] === true ? 1 : 0,
          job_type: jsonData['jobType'] === 'Walk-In' ? 1 : 2,
          status: jsonData['status']['name'] === 'In Progress' ? 3 : 1,
          handle_by: 3507, // erwin
          branch_service: 1, // sc cirebon
          created_at: jsonData['createdAt'],
        }

        resolve(mapJob)
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = (error) => {
      reject(error)
    }

    reader.readAsText(file)
  })
}

const getDateTime = (date: string, time: string, dateTimeDefault: string) => {
  // jika tidak ada date
  if (!date) return dateTimeDefault

  // lanjut jika ada date
  const timeValid = time || '08:00'

  // combine
  const timeString = date + ' ' + timeValid + ':00'

  const m = moment(timeString, 'DD/MM/YYYY HH:mm:ss', true)

  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : moment().format('YYYY-MM-DD HH:mm:ss')
}

// lifecycle
onMounted(() => {
  const handleDragEnter = (e: DragEvent) => {
    if (e.dataTransfer?.types.includes('Files')) {
      dragCounter++
      visible.value = true
    }
  }

  const handleDragLeave = () => {
    dragCounter--
    if (dragCounter <= 0) {
      visible.value = false
    }
  }

  const handleDropGlobal = () => {
    visible.value = false
    dragCounter = 0
  }

  window.addEventListener('dragenter', handleDragEnter)
  window.addEventListener('dragleave', handleDragLeave)
  window.addEventListener('drop', handleDropGlobal)
  window.addEventListener('dragover', (e) => e.preventDefault())

  onBeforeUnmount(() => {
    window.removeEventListener('dragenter', handleDragEnter)
    window.removeEventListener('dragleave', handleDragLeave)
    window.removeEventListener('drop', handleDropGlobal)
    window.removeEventListener('dragover', (e) => e.preventDefault())
  })
})
</script>

<template>
  <div
    v-if="visible"
    class="fixed m-4 inset-0 bg-white/90 dark:bg-black/90 text-black dark:text-white flex items-center justify-center z-50 border-4 border-stone-500 border-dashed rounded-2xl transition-colors duration-150"
    @dragover.prevent
    @drop.prevent="handleDrop"
  >
    <div class="flex flex-col">
      <ph-icon name="download-simple" :size="64" class="mx-auto text-blue-500"></ph-icon>
      <div class="text-center">
        <label-base size="2xl">Drop your file here!</label-base>
      </div>
    </div>
  </div>
</template>
